// services/global.service.js
import api from "../axiosIntercepture";

/**
 * Download sample/user guide from server and open in new tab.
 * - If server returns a binary file (pdf/xlsx/etc), it will be opened as a blob in a new tab.
 * - If server returns JSON with a URL, the URL will be opened.
 *
 * @param {string} type - e.g. "trainingsmall"
 * @param {string} [token] - optional bearer token override
 */
export const downloadSample = async (type = "trainingsmall", token = null) => {
    try {
        const config = {
            responseType: "blob", // expect file, fallback to JSON detection below
        };

        // If token passed explicitly, attach Authorization header
        if (token) {
            config.headers = { Authorization: `Bearer ${token}` };
        }

        const response = await api.post("global/downloadsample", { type }, config);

        const contentType = response?.headers?.["content-type"] || "";

        // If server returned JSON (sometimes sent as application/json), try parse it to get URL or message
        if (contentType.includes("application/json")) {
            // Read blob as text then JSON.parse
            const text = await response.data.text();
            let json;
            try {
                json = JSON.parse(text);
            } catch (e) {
                throw new Error("Invalid JSON response from download endpoint");
            }
            // Expect something like { success: true, data: "<url>" } or { url: "..." }
            const url = json?.data || json?.url || json?.downloadUrl;
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer");
                return { success: true, url };
            }
            throw new Error(json?.message || "Download endpoint returned JSON without URL");
        }

        // Otherwise treat as binary file
        const blob = new Blob([response.data], { type: contentType || "application/octet-stream" });

        // Use a filename from content-disposition if provided
        const contentDisposition = response?.headers?.["content-disposition"] || "";
        let filename = "download";
        const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)/i.exec(contentDisposition);
        if (match && match[1]) {
            filename = decodeURIComponent(match[1].replace(/['"]/g, ""));
        } else {
            // fallback based on type
            if (contentType.includes("pdf")) filename = "user_guide.pdf";
            else if (contentType.includes("excel") || contentType.includes("spreadsheet") || contentType.includes("sheet")) filename = "sample.xlsx";
            else filename = `${type}.bin`;
        }

        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, "_blank", "noopener,noreferrer");

        // If window.open is blocked (popup blocker), try download via anchor
        if (!newTab) {
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            // release url after small timeout
            setTimeout(() => window.URL.revokeObjectURL(url), 1000 * 10);
            return { success: true, downloaded: true, filename };
        }

        // revoke objectURL after short delay so tab can load it
        setTimeout(() => window.URL.revokeObjectURL(url), 1000 * 30);

        return { success: true, openedInNewTab: true, filename };
    } catch (err) {
        console.error("downloadSample error:", err);
        // Normalize error message
        const message = err?.response?.data?.message || err?.message || "Failed to download sample";
        throw new Error(message);
    }
};
